import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../core/data/redux/store';
import { Toast } from 'primereact/toast';
import { Col } from 'antd';
import { getAllProposals, updateProposal, deleteProposal } from '../../services/proposalServices';
import { getAllProjects } from '../../services/projectServices';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import TableNotFound from '../common/components/TableNotFound';
import { formatDate } from '../../utils/formatDate';
import ProposalsModal from '../../feature-module/admin/common/modals/proposals-modal';
import PinModal from '../../feature-module/admin/common/modals/pin-modal';
import { ProposalStatus, CurrencyType } from '../../services/proposalServices/types';

const ListProposals = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useRef<Toast>(null);
  
  const {
    proposals,
    isError,
    isLoading,
    errorMessage,
    proposalCreated,
    proposalUpdated,
  } = useSelector((state: any) => ({
    proposals: state.proposal.proposals,
    isError: state.proposal.isError,
    isLoading: state.proposal.isLoading,
    errorMessage: state.proposal.errorMessage,
    proposalCreated: state.proposal.proposalCreated,
    proposalUpdated: state.proposal.proposalUpdated,
  }));

  const { projects } = useSelector((state: any) => state.project);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [createdProposal, setCreatedProposal] = useState<any>(null);
  const [selectedProposalForPin, setSelectedProposalForPin] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Show error messages
  useEffect(() => {
    if (isError && errorMessage) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 50,
      });
    }
  }, [isError, errorMessage]);

  // Show success messages
  useEffect(() => {
    if (proposalCreated) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Proposal created successfully',
        life: 30,
      });
    }
  }, [proposalCreated]);

  useEffect(() => {
    if (proposalUpdated) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Proposal updated successfully',
        life: 30,
      });
    }
  }, [proposalUpdated]);

  const handleEdit = (rowData: any) => {
    setSelectedProposal(rowData);
    setIsEditModalOpen(true);
  };

  const handleDelete = (data: any) => {
    confirmDialog({
      message: `Are you sure you want to delete this proposal? This action cannot be undone.`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setIsDeleting(true);
        dispatch(deleteProposal(data.id))
          .then(() => {
            toast.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Proposal deleted successfully',
              life: 3000,
            });
          })
          .catch((error) => {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: error.message || 'Failed to delete proposal',
              life: 5000,
            });
          })
          .finally(() => {
            setIsDeleting(false);
          });
      },
      reject: () => { // User cancelled deletion
      }
    });
  };

  const handlePinModal = (rowData: any) => {
    setSelectedProposalForPin(rowData);
    setIsPinModalOpen(true);
  };

  const getProjectName = (projectId: string) => {
    const project = projects?.find((p: any) => p.id === projectId);
    return project?.name || projectId;
  };

  const formatPrice = (rowData: any) => {
    if (!rowData.price) return 'N/A';
    return `${rowData.currency_type || 'USD'} ${rowData.price.toLocaleString()}`;
  };

  const formatTimeline = (rowData: any) => {
    if (!rowData.expected_project_time) return 'N/A';
    return `${rowData.expected_project_time} weeks`;
  };

  const formatProjectLead = (rowData: any) => {
    return rowData.project_lead?.name || 'N/A';
  };

  const formatStages = (rowData: any) => {
    return rowData.stages?.length || 0;
  };

  const formatTimelines = (rowData: any) => {
    return rowData.timelines?.length || 0;
  };

  const formatServices = (rowData: any) => {
    return rowData.services?.length || 0;
  };

  const formatStatus = (rowData: any) => {
    const status = rowData.status;
    let statusClass = '';
    let statusText = status;

    switch (status) {
      case ProposalStatus.ACCEPTED:
        statusClass = 'badge-success';
        statusText = 'Accepted';
        break;
      case ProposalStatus.REJECTED:
        statusClass = 'badge-danger';
        statusText = 'Rejected';
        break;
      case ProposalStatus.PENDING:
      default:
        statusClass = 'badge-warning';
        statusText = 'Pending';
        break;
    }

    return (
      <span className={`badge ${statusClass}`}>
        {statusText}
      </span>
    );
  };

  const actionButton = (rowData: any) => {
    return (
      <div className="table-actions d-flex">
        <Link
          className="edit-table me-2"
          to="#"
          onClick={() => handleEdit(rowData)}
          title="Edit Proposal"
        >
          <Icon.Edit className="react-feather-custom" />
        </Link>
        <Link
          className="view-table me-2"
          to="#"
          onClick={() => handlePinModal(rowData)}
          title="View PIN Code"
        >
          <Icon.Lock className="react-feather-custom" />
        </Link>
        <Link
          onClick={() => {
            handleDelete(rowData);
          }}
          className="delete-table border-none"
          to="#"
          title="Delete Proposal"
          style={{ opacity: isDeleting ? 0.5 : 1 }}
        >
          <Icon.Trash2 className="react-feather-custom" />
        </Link>
      </div>
    );
  };

  const handleProposalCreated = (proposal: any) => {
    setCreatedProposal(proposal);
    setIsPinModalOpen(true);
  };

  useEffect(() => {
    dispatch(getAllProposals());
    dispatch(getAllProjects());
    
    return () => {
      dispatch({ type: 'RESET_PROPOSAL_ERRORS' });
    };
  }, [dispatch, proposalCreated, proposalUpdated]);

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="page-wrapper page-settings" style={{ paddingTop: '0px' }}>
        <div className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: 20,
              alignItems: 'center',
              paddingBottom: '2rem',
              flexWrap: 'wrap',
            }}
          >
            <Col
              style={{
                maxWidth: '700px',
              }}
            >
              <h5 style={{ margin: '0 0 2rem 0', padding: 0 }}>Proposals</h5>
              {proposals && proposals.length > 0 && (
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  Showing {proposals.length} proposal{proposals.length !== 1 ? 's' : ''}                </p>
              )}
            </Col>

            <button
                className="btn btn-primary"
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                disabled={isLoading}
              >
              <i className="fa fa-plus me-2" />
              Add Proposal
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="table-responsive">
                <DataTable
                  paginatorTemplate="RowsPerPageDropdown CurrentPageReport PrevPageLink PageLinks NextPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  value={proposals}
                  showGridlines
                  tableStyle={{ minWidth: '50rem' }}
                  loading={isLoading}
                  emptyMessage={
                    !isLoading && (
                      <TableNotFound
                        Icon={Icon.FileText}
                        title={
                          'No Proposals Found'
                        }
                        description="No proposals have been created yet. Click 'Add Proposal' to create your first proposal."
                      />
                    )
                  }
                  className={isDeleting ? 'table-disabled' : ''}                >
                  <Column
                    header="#"
                    body={(rowData, options) => options.rowIndex + 1}
                    style={{ width: '3rem' }}
                  ></Column>
                  
                  <Column
                    sortable
                    field="project_id"
                    header="Project"
                    body={(rowData) => getProjectName(rowData.project_id)}
                    style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column
                    sortable
                    field="status"
                    header="Status"
                    body={formatStatus}
                    style={{ maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="overview" 
                    header="Overview"
                    style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="price" 
                    header="Price"
                    body={formatPrice}
                    style={{ maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="expected_project_time" 
                    header="Timeline"
                    body={formatTimeline}
                    style={{ maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="created_at" 
                    header="Created At"
                    body={(rowData) => {
                      return formatDate(rowData.created_at?.split('T')[0] || '');
                    }}
                    style={{ maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="stages" 
                    header="Stages"
                    body={formatStages}
                    style={{ maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="timelines" 
                    header="Timelines"
                    body={formatTimelines}
                    style={{ maxWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  ></Column>
                  
                  <Column
                    field="action"
                    header="Actions"
                    body={actionButton}
                    style={{ width: '120px' }}
                  ></Column>
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ProposalsModal
        selectedProposal={selectedProposal}
        setIsAddModalOpen={setIsAddModalOpen}
        isAddModalOpen={isAddModalOpen}
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        onProposalCreated={handleProposalCreated}
      />
      {(createdProposal || selectedProposalForPin) && (
        <PinModal
          isOpen={isPinModalOpen}
          setIsOpen={setIsPinModalOpen}
          pinCode={createdProposal?.pin_code || selectedProposalForPin?.pin_code}
          proposalId={createdProposal?.id || selectedProposalForPin?.id}
        />
      )}
    </>
  );
};

export default ListProposals; 